import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

interface GlobalLoadingContextType {
  isLoading: boolean;
  startLoading: () => void;
  stopLoading: () => void;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

export const GlobalLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const loadingCount = useRef(0);
  const startTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const displayStartTimeRef = useRef<number>(0);
  const stopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const startLoading = useCallback(() => {
    loadingCount.current += 1;
    
    if (loadingCount.current === 1) {
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      if (!isLoadingRef.current) {
        startTimeoutRef.current = setTimeout(() => {
          setIsLoading(true);
          displayStartTimeRef.current = Date.now();
        }, 200);
      }
    }
  }, []);

  const stopLoading = useCallback(() => {
    loadingCount.current = Math.max(0, loadingCount.current - 1);

    if (loadingCount.current === 0) {
      if (startTimeoutRef.current) {
        clearTimeout(startTimeoutRef.current);
        startTimeoutRef.current = null;
      }

      if (isLoadingRef.current) {
        const timeElapsed = Date.now() - displayStartTimeRef.current;
        const minDisplayTime = 450;
        
        if (timeElapsed < minDisplayTime) {
          stopTimeoutRef.current = setTimeout(() => {
            setIsLoading(false);
          }, minDisplayTime - timeElapsed);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ isLoading, startLoading, stopLoading }}>
      {children}
    </GlobalLoadingContext.Provider>
  );
};

export const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (context === undefined) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
};

export const GlobalLoaderTrigger = () => {
  const { startLoading, stopLoading } = useGlobalLoading();
  React.useEffect(() => {
    startLoading();
    return () => stopLoading();
  }, [startLoading, stopLoading]);
  return null;
};
